import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { Modal, Button, Form } from 'react-bootstrap';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Checkbox,
    FormLabel,
    InputAdornment,
    IconButton
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import toastr from 'toastr';
import { fetchFacilitiesForOrders } from '../../../services/clinicPortalServices/facilityServices';
import { getAllResultsData, updateResultValue } from '../../../services/clinicPortalServices/allResultsService';
import { getOrderDataById } from '../../../services/clinicPortalServices/orderEditService';
import OrderViewPopup, { SubOrderDrawer } from '../orders/clinicOrderGrid/orderViewPopup';
import Breadcrumb from './breadcrumb';

const agTheme = themeQuartz.withPart(colorSchemeLight);

const FLAG_OPTIONS = ['H', 'HH', 'L', 'LL', 'A', 'POS', 'NEG'];

class AllResultsGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rowData: [],
            loading: false,
            facilities: [],
            facilityId: [],
            searchFilters: {
                facility_id: [],
                from_date: null,
                to_date: null,
                dateRange: null,
                selectedFlags: [],
            },
            quickFilterText: '',
            paginationPageSize: 50,
            pickerOpened: false,
            showEditModal: false,
            editRow: null,
            editValue: '',
            editUnit: '',
            editFlag: '',
            savingResult: false,
            showOrderViewPopup: false,
            orderViewDetails: null,
            showDrawer: false,
            drawerOrderDetails: null,
        };

        this.columnDefs = [
            {
                headerName: 'Order ID',
                field: 'lab_order_id',
                sortable: true,
                filter: true,
                width: 130,
                pinned: 'left',
            },
            {
                headerName: 'Actions',
                field: '_id',
                width: 110,
                sortable: false,
                filter: false,
                cellRenderer: (params) => {
                    return (
                        <div className="d-flex align-items-center gap-1">
                            <button
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                title="View Order"
                                onClick={() => params.context.openViewPopup(params.data)}
                            >
                                <i className="fas fa-eye"></i>
                            </button>
                            <button
                                className="btn btn-sm btn-outline-primary py-0 px-2"
                                title="Open in side panel"
                                onClick={() => params.context.openDrawer(params.data)}
                            >
                                <i className="fas fa-columns"></i>
                            </button>
                            <button
                                className="btn btn-sm btn-outline-primary py-0 px-2"
                                title="Edit Result"
                                onClick={() => params.context.openEditModal(params.data)}
                            >
                                <i className="fas fa-pen"></i>
                            </button>
                        </div>
                    );
                },
            },
            { headerName: 'Patient', field: 'patient_name', sortable: true, filter: true, width: 180 },
            { headerName: 'DOB', field: 'dob', sortable: true, filter: true, width: 110 },
            { headerName: 'Test Name', field: 'test_name', sortable: true, filter: true, width: 220 },
            { headerName: 'Analyte', field: 'analyte_name', sortable: true, filter: true, width: 180 },
            {
                headerName: 'Result Value',
                field: 'result_value',
                sortable: true,
                filter: true,
                width: 130,
                cellRenderer: (params) => {
                    if (params.value === null || params.value === undefined || params.value === '') {
                        return <span style={{ color: '#aaa', fontStyle: 'italic' }}>—</span>;
                    }
                    return <span>{String(params.value)}</span>;
                },
            },
            { headerName: 'Unit', field: 'unit', sortable: true, filter: true, width: 100 },
            {
                headerName: 'Flag',
                field: 'flag',
                sortable: true,
                filter: true,
                width: 90,
                cellRenderer: (params) => {
                    if (!params.value) return null;
                    const color =
                        params.value === 'H' || params.value === 'HH'
                            ? '#dc3545'
                            : params.value === 'L' || params.value === 'LL'
                            ? '#0d6efd'
                            : '#6c757d';
                    return <span style={{ color, fontWeight: '600' }}>{params.value}</span>;
                },
            },
            { headerName: 'Ref Range', field: 'reference_range', sortable: true, filter: true, width: 150 },
            { headerName: 'Collected', field: 'collected_date', sortable: true, filter: true, width: 130 },
            { headerName: 'Facility', field: 'facility_name', sortable: true, filter: true, width: 180 },
        ];

        this.defaultColDef = { resizable: true };
        this.components = {};
    }

    componentDidMount() {
        fetchFacilitiesForOrders().then((res) => {
            const facilities = res.data || res || [];
            const allIds = facilities.map((f) => f._id);
            this.setState(
                {
                    facilities,
                    facilityId: allIds,
                    searchFilters: { ...this.state.searchFilters, facility_id: allIds },
                },
                () => this.loadData()
            );
        });
    }

    loadData = () => {
        const { searchFilters } = this.state;
        this.setState({ loading: true });
        const params = {
            facility_id: searchFilters.facility_id,
            ...(searchFilters.from_date ? { from_date: searchFilters.from_date } : {}),
            ...(searchFilters.to_date ? { to_date: searchFilters.to_date } : {}),
        };
        getAllResultsData(params)
            .then((res) => {
                let rows = res.data || [];
                // Client-side flag filter
                const { selectedFlags } = searchFilters;
                if (selectedFlags && selectedFlags.length > 0) {
                    rows = rows.filter((r) => selectedFlags.includes(r.flag));
                }
                this.setState({ rowData: rows, loading: false });
            })
            .catch(() => {
                this.setState({ loading: false });
                toastr.error('Failed to load results.');
            });
    };

    handleFacilityChange = (e) => {
        const { facilities } = this.state;
        let value = e.target.value;
        if (value.includes('all')) {
            const allIds = facilities.map((f) => f._id);
            const isAllSelected = this.state.facilityId.length === allIds.length;
            value = isAllSelected ? [] : allIds;
        }
        this.setState(
            {
                facilityId: value,
                searchFilters: { ...this.state.searchFilters, facility_id: value },
            }
        );
    };

    handleFlagChange = (e) => {
        let value = e.target.value;
        if (value.includes('all')) {
            const isAllSelected = this.state.searchFilters.selectedFlags.length === FLAG_OPTIONS.length;
            value = isAllSelected ? [] : [...FLAG_OPTIONS];
        }
        this.setState({ searchFilters: { ...this.state.searchFilters, selectedFlags: value } });
    };

    updateDateRange = (range) => {
        let from_date, to_date;
        const fmt = (m) => m.format('YYYY-MM-DD') + 'T00:00';
        if (range === 'today') {
            from_date = fmt(moment());
            to_date = fmt(moment().add(1, 'days'));
        } else if (range === 'week') {
            from_date = fmt(moment().startOf('week'));
            to_date = fmt(moment().endOf('week').add(1, 'days'));
        } else if (range === 'month') {
            from_date = fmt(moment().startOf('month'));
            to_date = fmt(moment().endOf('month').add(1, 'days'));
        } else if (range === 'quarter') {
            from_date = fmt(moment().startOf('quarter'));
            to_date = fmt(moment().endOf('quarter').add(1, 'days'));
        }
        this.setState(
            { searchFilters: { ...this.state.searchFilters, from_date, to_date, dateRange: range } },
            () => this.loadData()
        );
    };

    handleDateChange = (dateVal, field) => {
        const formatted = moment(dateVal).format('YYYY-MM-DD') + 'T' + moment(dateVal).format('HH:mm');
        this.setState({
            searchFilters: { ...this.state.searchFilters, [field]: formatted, dateRange: 'custom' },
        });
    };

    applyFilters = () => {
        this.loadData();
    };

    clearFilters = () => {
        if (this.gridApi) this.gridApi.setFilterModel(null);
        this.setState({ quickFilterText: '' });
        if (document.getElementById('ar-grid-search')) {
            document.getElementById('ar-grid-search').value = '';
        }
    };

    onFilterTextChange = (e) => {
        const val = e.target.value;
        this.setState({ quickFilterText: val });
        if (this.gridApi) this.gridApi.setQuickFilter(val);
    };

    onPageSizeChanged = (e) => {
        const size = parseInt(e.target.value, 10);
        if (!isNaN(size) && size > 0) {
            this.setState({ paginationPageSize: size });
            if (this.gridApi) this.gridApi.paginationSetPageSize(size);
        }
    };

    onGridReady = (params) => {
        this.gridApi = params.api;
    };

    openViewPopup = (rowData) => {
        const orderId = rowData && rowData.order_id;
        if (!orderId) return;
        getOrderDataById(orderId).then((res) => {
            if (res && res.data) {
                this.setState({ orderViewDetails: res.data, showOrderViewPopup: true });
            }
        }).catch(() => toastr.error('Could not load order details.'));
    };

    onRowClicked = (params) => {
        // Don't open popup when clicking the Actions column
        if (params.column && params.column.colId === '_id') return;
        this.openViewPopup(params.data);
    };

    openDrawer = (rowData) => {
        const orderId = rowData && rowData.order_id;
        if (!orderId) return;
        getOrderDataById(orderId).then((res) => {
            if (res && res.data) {
                this.setState({ drawerOrderDetails: res.data, showDrawer: true });
            }
        }).catch(() => {});
    };

    openEditModal = (row) => {
        this.setState({
            showEditModal: true,
            editRow: row,
            editValue: row.result_value !== undefined && row.result_value !== null ? String(row.result_value) : '',
            editUnit: row.unit || '',
            editFlag: row.flag || '',
        });
    };

    closeEditModal = () => {
        this.setState({ showEditModal: false, editRow: null, savingResult: false });
    };

    handleSaveResult = () => {
        const { editRow, editValue, editUnit, editFlag } = this.state;
        if (!editRow) return;
        this.setState({ savingResult: true });
        updateResultValue({
            orderId: editRow.order_id,
            value_index: editRow.value_index,
            result_value: editValue,
            unit: editUnit,
            flag: editFlag,
        })
            .then(() => {
                toastr.success('Result updated successfully.');
                this.setState({ savingResult: false, showEditModal: false });
                this.loadData();
            })
            .catch((err) => {
                toastr.error(err?.response?.data?.message || 'Failed to save result.');
                this.setState({ savingResult: false });
            });
    };

    render() {
        const {
            rowData, loading, facilities, facilityId, searchFilters,
            paginationPageSize, quickFilterText,
            showEditModal, editRow, editValue, editUnit, editFlag, savingResult,
            pickerOpened, showOrderViewPopup, orderViewDetails,
        } = this.state;

        const { from_date, to_date, dateRange, selectedFlags } = searchFilters;

        return (
            <div className="clinicPortal">
                <div className="clinicWorkSpace">
                    <Breadcrumb />

                    {/* ── Filter Bar ── */}
                    <div className="row mt-4 px-3">
                        {/* Left: Facility + Flag */}
                        <div className="col-md-9 col-12" style={{ borderRight: '2px solid lightGray' }}>
                            <div className="row">
                                {/* Facility */}
                                <div className="col-md-6 col-12 mb-3">
                                    <FormControl className="filter-select-bar" variant="outlined">
                                        <InputLabel id="ar-facility-label">Select Facility</InputLabel>
                                        <Select
                                            labelId="ar-facility-label"
                                            id="ar-facility-select"
                                            value={facilityId}
                                            onChange={this.handleFacilityChange}
                                            label="Select Facility"
                                            multiple
                                            renderValue={(selected) => {
                                                if (selected.length === facilities.length && facilities.length > 0) {
                                                    return 'All Facilities';
                                                }
                                                return facilities
                                                    .filter((f) => selected.includes(f._id))
                                                    .map((f) => f.name)
                                                    .join(', ');
                                            }}
                                        >
                                            <MenuItem
                                                value="all"
                                                key="all"
                                                className={facilityId.length === facilities.length ? 'Mui-selected' : ''}
                                            >
                                                <Checkbox checked={facilityId.length === facilities.length && facilities.length > 0} />
                                                All Facilities
                                            </MenuItem>
                                            {facilities.map((fac) => (
                                                <MenuItem key={fac._id} value={fac._id}>
                                                    <Checkbox checked={facilityId.includes(fac._id)} />
                                                    {fac.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Flag Filter */}
                                    <FormControl className="filter-select-bar mt-2" variant="outlined">
                                        <InputLabel id="ar-flag-label">Select Flag</InputLabel>
                                        <Select
                                            labelId="ar-flag-label"
                                            value={selectedFlags}
                                            onChange={this.handleFlagChange}
                                            label="Select Flag"
                                            multiple
                                            renderValue={(selected) => {
                                                if (selected.length === 0) return 'All Flags';
                                                if (selected.length === FLAG_OPTIONS.length) return 'All Flags';
                                                return selected.join(', ');
                                            }}
                                        >
                                            <MenuItem
                                                value="all"
                                                key="all"
                                                className={selectedFlags.length === FLAG_OPTIONS.length ? 'Mui-selected' : ''}
                                            >
                                                <Checkbox checked={selectedFlags.length === FLAG_OPTIONS.length} />
                                                All Flags
                                            </MenuItem>
                                            {FLAG_OPTIONS.map((flag) => (
                                                <MenuItem key={flag} value={flag}>
                                                    <Checkbox checked={selectedFlags.includes(flag)} />
                                                    {flag}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                {/* Date Range */}
                                <div className="col-md-6 col-12 mb-3" style={{ marginTop: '-10px' }}>
                                    <div className="row">
                                        <div className="col-12">
                                            <FormLabel style={{ marginBottom: '4px' }}>Date Range</FormLabel>
                                        </div>
                                        <div className="dateRangeBar mb-3">
                                            {['today', 'week', 'month', 'quarter'].map((r) => (
                                                <button
                                                    key={r}
                                                    className={`btn btn-info dateRangeBtn mx-1 mb-2${dateRange === r ? ' active' : ''}`}
                                                    onClick={() => this.updateDateRange(r)}
                                                >
                                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="dateRangeBar dateRangeBarPickers">
                                            <MobileDateTimePicker
                                                value={pickerOpened ? this.state.pickerFrom : from_date}
                                                onChange={(dt) => this.setState({ pickerFrom: dt })}
                                                onOpen={() => this.setState({ pickerFrom: from_date, pickerOpened: true })}
                                                onClose={() => this.setState({ pickerOpened: false })}
                                                onAccept={(dt) => this.handleDateChange(dt.$d || dt, 'from_date')}
                                                label="From Date"
                                                renderInput={(props) => (
                                                    <TextField
                                                        {...props}
                                                        className="ml-1 mr-2 mt-2"
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton edge="end"><EventIcon /></IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                            <MobileDateTimePicker
                                                value={pickerOpened ? this.state.pickerTo : to_date}
                                                onChange={(dt) => this.setState({ pickerTo: dt })}
                                                onOpen={() => this.setState({ pickerTo: to_date, pickerOpened: true })}
                                                onClose={() => this.setState({ pickerOpened: false })}
                                                onAccept={(dt) => this.handleDateChange(dt.$d || dt, 'to_date')}
                                                label="To Date"
                                                renderInput={(props) => (
                                                    <TextField
                                                        {...props}
                                                        className="mt-2"
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton edge="end"><EventIcon /></IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Search + Page Size + Actions */}
                        <div className="col-md-3 col-12 grid-buttons">
                            <div className="text-center">
                                <TextField
                                    id="ar-grid-search"
                                    label="Current Grid Search"
                                    className="filter-search-bar mt-2"
                                    InputLabelProps={{ shrink: true }}
                                    margin="none"
                                    size="small"
                                    onChange={this.onFilterTextChange}
                                />
                                <div>
                                    <TextField
                                        sx={{ width: '100px' }}
                                        label="Page Size"
                                        className="mt-3"
                                        InputLabelProps={{ shrink: true }}
                                        type="number"
                                        margin="none"
                                        size="small"
                                        defaultValue={paginationPageSize}
                                        onChange={this.onPageSizeChanged}
                                    />
                                    <button
                                        className="btn btn-primary button-info-grid"
                                        onClick={this.applyFilters}
                                        disabled={loading}
                                    >
                                        <i className={`fas fa-sync-alt mr-1${loading ? ' fa-spin' : ''}`}></i> Refresh
                                    </button>
                                    <button
                                        className="btn btn-primary button-info-grid"
                                        onClick={this.clearFilters}
                                    >
                                        <i className="fa fa-times" aria-hidden="true"></i> Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Grid ── */}
                    <div className="d-flex justify-content-center gap-2 mt-1">
                        <button className="btn btn-sm btn-outline-secondary mr-2" onClick={() => { const v = document.querySelector('#ar-grid .ag-body-horizontal-scroll-viewport'); if (v) v.scrollBy({ left: -200, behavior: 'smooth' }); }}>
                            <i className="fas fa-chevron-left"></i> Scroll Left
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { const v = document.querySelector('#ar-grid .ag-body-horizontal-scroll-viewport'); if (v) v.scrollBy({ left: 200, behavior: 'smooth' }); }}>
                            Scroll Right <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                    <div className="card mt-3">
                        <div
                            id="ar-grid"
                            style={{ height: 'calc(100vh - 380px)', width: '100%' }}
                        >
                            <AgGridReact
                                theme={agTheme}
                                rowData={rowData}
                                columnDefs={this.columnDefs}
                                defaultColDef={this.defaultColDef}
                                components={this.components}
                                context={{ openEditModal: this.openEditModal, openViewPopup: this.openViewPopup, openDrawer: this.openDrawer }}
                                pagination={true}
                                paginationPageSize={paginationPageSize}
                                rowHeight={36}
                                quickFilterText={quickFilterText}
                                onGridReady={this.onGridReady}
                                onRowClicked={this.onRowClicked}
                                rowStyle={{ cursor: 'pointer' }}
                                sideBar={{ toolPanels: ['columns'] }}
                                overlayLoadingTemplate={
                                    '<span class="ag-overlay-loading-center">Loading results...</span>'
                                }
                                overlayNoRowsTemplate={
                                    '<span style="padding:20px">No results found.</span>'
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* ── Edit Result Modal ── */}
                <Modal show={showEditModal} onHide={this.closeEditModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="fas fa-edit mr-2 text-primary"></i>Edit Result
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {editRow && (
                            <>
                                <div className="p-3 mb-3 bg-light rounded border">
                                    <div><strong>Order:</strong> {editRow.lab_order_id}</div>
                                    <div><strong>Patient:</strong> {editRow.patient_name}</div>
                                    <div><strong>Test:</strong> {editRow.test_name}</div>
                                    <div><strong>Analyte:</strong> {editRow.analyte_name}</div>
                                    {editRow.reference_range && (
                                        <div><strong>Ref Range:</strong> {editRow.reference_range}</div>
                                    )}
                                </div>
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label><strong>Result Value</strong></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => this.setState({ editValue: e.target.value })}
                                            placeholder="Enter result value"
                                            autoFocus
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label><strong>Unit</strong></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={editUnit}
                                            onChange={(e) => this.setState({ editUnit: e.target.value })}
                                            placeholder="e.g. ng/mL"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label><strong>Flag</strong></Form.Label>
                                        <Form.Select
                                            value={editFlag}
                                            onChange={(e) => this.setState({ editFlag: e.target.value })}
                                        >
                                            <option value="">None</option>
                                            <option value="H">H – High</option>
                                            <option value="HH">HH – Critical High</option>
                                            <option value="L">L – Low</option>
                                            <option value="LL">LL – Critical Low</option>
                                            <option value="A">A – Abnormal</option>
                                            <option value="POS">POS – Positive</option>
                                            <option value="NEG">NEG – Negative</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Form>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closeEditModal}>Cancel</Button>
                        <Button variant="primary" onClick={this.handleSaveResult} disabled={savingResult}>
                            {savingResult ? (
                                <><span className="spinner-border spinner-border-sm mr-1"></span>Saving...</>
                            ) : (
                                <><i className="fas fa-save mr-1"></i>Save Result</>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ── Order View Popup ── */}
                <OrderViewPopup
                    show={showOrderViewPopup}
                    handleClose={() => this.setState({ showOrderViewPopup: false, orderViewDetails: null })}
                    data={orderViewDetails}
                    context={{}}
                    type="order"
                    orderDetails={orderViewDetails}
                />
                {/* ── Side drawer ── */}
                {this.state.showDrawer && this.state.drawerOrderDetails && (
                    <SubOrderDrawer
                        orderDetails={this.state.drawerOrderDetails}
                        onClose={() => this.setState({ showDrawer: false, drawerOrderDetails: null })}
                    />
                )}
            </div>
        );
    }
}

export default AllResultsGrid;
